import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  VStack,
  Heading,
  SimpleGrid,
  Stack,
  Separator,
  Flex,
} from '@chakra-ui/react';
import { CustomInput } from '../../custom/CustomInput';
import { CustomSelect } from '../../custom/CustomSelect';
import { CustomButton } from '../../custom/CustomButton';
import { CustomRadioGroup } from '../../custom/CustomRadioGroup';
import { CustomCheckbox } from '../../custom/CustomCheckbox';
import { Field } from '../../custom/CustomField';
import { PasswordInput } from '../../ui/password-input';

const CompanyInformation = ({ onNext, loading, initialValues }) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: initialValues
  });

  const onSubmit = (data) => {
    // Transform independent product experience fields into an array
    const productsExperience = [];
    if (data.pe_Spot) productsExperience.push('Spot');
    if (data.pe_Futures) productsExperience.push('Futures');
    if (data.pe_CFDs) productsExperience.push('CFDs');
    if (data.pe_Shares) productsExperience.push('Shares');
    if (data.pe_Indices) productsExperience.push('Indices');

    const finalData = { ...data, productsExperience };
    // Clean up temporary fields
    delete finalData.pe_Spot;
    delete finalData.pe_Futures;
    delete finalData.pe_CFDs;
    delete finalData.pe_Shares;
    delete finalData.pe_Indices;

    onNext(finalData);
  };

  const yesNoOptions = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' }
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={8} align="stretch">
        
        {/* Basic Company Information */}
        <Box>
          <Heading size="md" mb={4} color="blue.600">Basic Company Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} columnGap={8}>
            <CustomInput 
              label="Corporate Name (As per License)" 
              error={errors.corporateName} 
              required
              placeholder="Enter corporate name"
              {...register('corporateName', { required: 'Corporate Name is required' })}
            />

            <CustomInput 
              label="Type of Incorporation" 
              error={errors.typeOfIncorporation} 
              required
              placeholder="e.g., LLC, Corporation"
              {...register('typeOfIncorporation', { required: 'Type of Incorporation is required' })}
            />

            <CustomSelect 
              label="Ownership Type" 
              error={errors.ownershipType} 
              required
              placeholder="Select option"
              options={["Private", "Public", "Government", "Other"]}
              {...register('ownershipType', { required: 'Required' })}
            />

            <CustomInput 
              label="Country of Incorporation" 
              error={errors.countryOfIncorporation} 
              required
              placeholder="Enter country"
              {...register('countryOfIncorporation', { required: 'Country is required' })}
            />

            <Box gridColumn={{ md: 'span 2' }}>
                <CustomInput 
                  label="Company Registered Address" 
                  error={errors.companyRegisteredAddress} 
                  required
                  placeholder="Enter full address"
                  {...register('companyRegisteredAddress', { required: 'Address is required' })}
                />
            </Box>

            <CustomInput 
              label="Incorporation Date" 
              error={errors.incorporationDate} 
              required
              type="date"
              {...register('incorporationDate', { required: 'Date is required' })}
            />

            <CustomInput 
              label="Incorporation No." 
              error={errors.incorporationNo} 
              required
              placeholder="Enter incorporation number"
              {...register('incorporationNo', { required: 'Incorporation No. is required' })}
            />

            <CustomInput 
              label="Number of Shareholders" 
              error={errors.numberOfShareholders} 
              required
              type="number"
              placeholder="Enter number"
              {...register('numberOfShareholders', { required: 'Required' })}
            />

            <CustomInput 
              label="Number of Staff" 
              error={errors.numberOfStaff} 
              required
              type="number"
              placeholder="Enter number"
              {...register('numberOfStaff', { required: 'Required' })}
            />

             <CustomInput 
              label="Mobile" 
              error={errors.mobile} 
              required
              type="tel"
              placeholder="Enter mobile number"
              {...register('mobile', { required: 'Mobile is required' })}
            />

            <CustomInput 
              label="Email" 
              error={errors.email} 
              required
              type="email"
              placeholder="Enter email address"
              {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                  }
              })}
            />
          </SimpleGrid>
        </Box>

        <Separator />

        {/* Regulatory Information */}
        <Box>
          <Heading size="md" mb={4} color="blue.600">Regulatory Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} columnGap={8}>
            <CustomInput 
              label="Regulatory Authority" 
              error={errors.regulatoryAuthority}
              placeholder="Enter authority name"
              {...register('regulatoryAuthority')} 
            />

            <CustomInput 
              label="Activity License No." 
              error={errors.activityLicenseNo}
              placeholder="Enter license no"
              {...register('activityLicenseNo')} 
            />

            <CustomInput 
              label="License Expiry" 
              error={errors.licenseExpiry}
              type="date" 
              {...register('licenseExpiry')} 
            />

            <CustomInput 
              label="Main Business Activity" 
              error={errors.mainBusinessActivity}
              placeholder="Enter activity"
              {...register('mainBusinessActivity')} 
            />
          </SimpleGrid>
        </Box>

        <Separator />

        {/* Financial Information */}
        <Box>
          <Heading size="md" mb={4} color="blue.600">Financial Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} columnGap={8}>
            <CustomSelect 
              label="Company Annual Turnover (USD)" 
              error={errors.annualTurnover} 
              required
              placeholder="Select range"
              {...register('annualTurnover', { required: 'Required' })}
            >
                <option value="1000-100000">1,000 - 100,000</option>
                <option value="100000-1000000">100,000 - 1,000,000</option>
                <option value=">1000000">&gt; 1,000,000</option>
            </CustomSelect>

            <CustomSelect 
              label="Company Net Assets (USD)" 
              error={errors.netAssets} 
              required
              placeholder="Select range"
              {...register('netAssets', { required: 'Required' })}
            >
                <option value="1000-100000">1,000 - 100,000</option>
                <option value="100000-1000000">100,000 - 1,000,000</option>
                <option value=">1000000">&gt; 1,000,000</option>
            </CustomSelect>

            <CustomInput 
              label="Approx. Liquid Portfolio (USD)" 
              error={errors.liquidPortfolio}
              type="number" 
              placeholder="Enter amount" 
              {...register('liquidPortfolio')} 
            />

            <CustomInput 
              label="Funds Available for Trading" 
              error={errors.fundsAvailable}
              type="number" 
              placeholder="Enter amount" 
              {...register('fundsAvailable')} 
            />

            <CustomSelect 
              label="Source of Company's Wealth" 
              error={errors.sourceOfWealth} 
              required
              placeholder="Select source"
              {...register('sourceOfWealth', { required: 'Required' })}
            >
                <option value="Business Profit">Business Profit</option>
                <option value="Loan">Loan</option>
                <option value="Saving">Saving</option>
                <option value="Others">Others</option>
            </CustomSelect>

            <CustomInput 
              label="Corporate Tax Number" 
              error={errors.corporateTaxNumber}
              placeholder="Enter tax number" 
              {...register('corporateTaxNumber')} 
            />
             
            <Field 
                label="Password" 
                invalid={!!errors.password} 
                errorText={errors.password?.message} 
                required
                width="100%"
                mb={4}
            >
                <PasswordInput 
                    placeholder="Enter password" 
                    padding="12px 16px"
                    borderRadius="8px"
                    bg="white"
                    fontSize="md"
                    {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })} 
                />
            </Field>

            <Field 
                label="Confirm Password" 
                invalid={!!errors.confirmPassword} 
                errorText={errors.confirmPassword?.message} 
                required
                width="100%"
                mb={4}
            >
                <PasswordInput 
                    placeholder="Confirm password" 
                    padding="12px 16px"
                    borderRadius="8px"
                    bg="white"
                    fontSize="md"
                    {...register('confirmPassword', { 
                        required: 'Confirm Password is required',
                        validate: (val) => {
                            if (watch('password') !== val) {
                                return "Your passwords do not match";
                            }
                        }
                    })} 
                />
            </Field>
          </SimpleGrid>
        </Box>

        <Separator />

        {/* Trading Information */}
        <Box>
          <Heading size="md" mb={4} color="blue.600">Trading Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} columnGap={8}>
            <CustomSelect 
              label="Primary Purpose of Account" 
              error={errors.primaryPurpose} 
              required
              placeholder="Select purpose"
              {...register('primaryPurpose', { required: 'Required' })}
            >
                <option value="Hedging">Hedging</option>
                <option value="Capital Appreciation">Capital Appreciation</option>
                <option value="Speculative Trading">Speculative Trading</option>
            </CustomSelect>

            <CustomRadioGroup 
                label="Prior Experience in Trading Derivatives?"
                name="priorExperience"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />

            <CustomRadioGroup 
                label="Experience in Trading Leveraged Products?"
                name="leveragedExperience"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />

            <CustomRadioGroup 
                label="Understand Risks?"
                name="understandRisks"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />
            
            <Field as="fieldset" label="Products Experience" gridColumn={{ base: "span 1", md: "span 2" }}>
                <Stack spacing={[1, 3]} direction={['column', 'row']}>
                    <Controller
                        name="pe_Spot"
                        control={control}
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                            <Box 
                                p={2} 
                                borderWidth="1px" 
                                borderRadius="md" 
                                borderColor={value ? "blue.500" : "gray.200"}
                                bg={value ? "blue.50" : "white"}
                                _hover={{ borderColor: "blue.300", bg: value ? "blue.50" : "gray.50" }}
                                transition="all 0.2s"
                                cursor="pointer"
                                onClick={() => onChange(!value)}
                            >
                                <CustomCheckbox 
                                    id="pe_Spot"
                                    name="pe_Spot"
                                    inputProps={{ id: 'input_pe_Spot' }}
                                    checked={!!value}
                                    pointerEvents="none" // Prevent double toggling from checkbox click
                                    readOnly
                                >Spot</CustomCheckbox>
                            </Box>
                        )}
                    />
                    <Controller
                        name="pe_Futures"
                        control={control}
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                            <Box 
                                p={2} 
                                borderWidth="1px" 
                                borderRadius="md" 
                                borderColor={value ? "blue.500" : "gray.200"}
                                bg={value ? "blue.50" : "white"}
                                _hover={{ borderColor: "blue.300", bg: value ? "blue.50" : "gray.50" }}
                                transition="all 0.2s"
                                cursor="pointer"
                                onClick={() => onChange(!value)}
                            >
                                <CustomCheckbox 
                                    id="pe_Futures"
                                    name="pe_Futures"
                                    inputProps={{ id: 'input_pe_Futures' }}
                                    checked={!!value}
                                    pointerEvents="none"
                                    readOnly
                                >Futures</CustomCheckbox>
                            </Box>
                        )}
                    />
                    <Controller
                        name="pe_CFDs"
                        control={control}
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                            <Box 
                                p={2} 
                                borderWidth="1px" 
                                borderRadius="md" 
                                borderColor={value ? "blue.500" : "gray.200"}
                                bg={value ? "blue.50" : "white"}
                                _hover={{ borderColor: "blue.300", bg: value ? "blue.50" : "gray.50" }}
                                transition="all 0.2s"
                                cursor="pointer"
                                onClick={() => onChange(!value)}
                            >
                                <CustomCheckbox 
                                    id="pe_CFDs"
                                    name="pe_CFDs"
                                    inputProps={{ id: 'input_pe_CFDs' }}
                                    checked={!!value}
                                    pointerEvents="none"
                                    readOnly
                                >CFDs</CustomCheckbox>
                            </Box>
                        )}
                    />
                    <Controller
                        name="pe_Shares"
                        control={control}
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                            <Box 
                                p={2} 
                                borderWidth="1px" 
                                borderRadius="md" 
                                borderColor={value ? "blue.500" : "gray.200"}
                                bg={value ? "blue.50" : "white"}
                                _hover={{ borderColor: "blue.300", bg: value ? "blue.50" : "gray.50" }}
                                transition="all 0.2s"
                                cursor="pointer"
                                onClick={() => onChange(!value)}
                            >
                                <CustomCheckbox 
                                    id="pe_Shares"
                                    name="pe_Shares"
                                    inputProps={{ id: 'input_pe_Shares' }}
                                    checked={!!value}
                                    pointerEvents="none"
                                    readOnly
                                >Shares</CustomCheckbox>
                            </Box>
                        )}
                    />
                    <Controller
                        name="pe_Indices"
                        control={control}
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                            <Box 
                                p={2} 
                                borderWidth="1px" 
                                borderRadius="md" 
                                borderColor={value ? "blue.500" : "gray.200"}
                                bg={value ? "blue.50" : "white"}
                                _hover={{ borderColor: "blue.300", bg: value ? "blue.50" : "gray.50" }}
                                transition="all 0.2s"
                                cursor="pointer"
                                onClick={() => onChange(!value)}
                            >
                                <CustomCheckbox 
                                    id="pe_Indices"
                                    name="pe_Indices"
                                    inputProps={{ id: 'input_pe_Indices' }}
                                    checked={!!value}
                                    pointerEvents="none"
                                    readOnly
                                >Indices</CustomCheckbox>
                            </Box>
                        )}
                    />
                </Stack>
            </Field>

            <CustomRadioGroup 
                label="Account with Moneyplant FX/Affiliates?"
                name="accountWithVPFX"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />

            <CustomRadioGroup 
                label="Past Complaints/Legal Issues?"
                name="pastComplaints"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />

            <CustomRadioGroup 
                label="US Entity/Tax Resident?"
                name="usEntity"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />

            <CustomRadioGroup 
                label="Bankruptcy/Insolvency?"
                name="bankruptcy"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />

            <CustomRadioGroup 
                label="Criminal/Regulatory Proceedings?"
                name="criminalProceedings"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />

            <CustomRadioGroup 
                label="Broker/Intermediary?"
                name="brokerIntermediary"
                control={control}
                defaultValue="No"
                options={yesNoOptions}
            />
          </SimpleGrid>
        </Box>

        <Flex justify="space-between" mt={8}>
          <CustomButton
            type="button"
            variant="outline"
            colorScheme="gray"
            borderWidth="2px"
            borderColor="gray.300"
            bg="white"
            _hover={{
              bg: "gray.50",
              borderColor: "gray.400"
            }}
            _active={{
              bg: "gray.100"
            }}
            onClick={() => navigate('/login')}
          >
            Back to Login
          </CustomButton>
          <CustomButton
            type="submit"
            colorScheme="blue"
            size="lg"
            isLoading={loading}
            loadingText="Saving..."
          >
            Next: Corporate Officers
          </CustomButton>
        </Flex>
      </VStack>
    </form>
  );
};

export default CompanyInformation;
